/**
 * SendEmailDialog — fires any active email template against the current ticket.
 *
 * - Lists active templates (with the "Custom" badge for non-seed entries).
 * - Renders a server-side preview so the user sees exactly what will be sent.
 * - Accepts To / CC / BCC as comma-separated emails; pre-fills To from the
 *   ticket's customer email when present.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

function splitEmails(raw) {
  return (raw || "")
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function SendEmailDialog({ open, onOpenChange, ticket, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [picked, setPicked] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/email-templates").then((r) => {
      const list = (r.data || []).filter((t) => t.active !== false);
      setTemplates(list);
      if (!picked && list[0]) setPicked(list[0].key);
    }).catch(() => toast.error("Couldn't load templates"))
      .finally(() => setLoading(false));
    setTo(ticket?.email_address || "");
    setCc("");
    setBcc("");
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!picked || !open) return;
    api.post(`/email-templates/${picked}/preview`, {})
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [picked, open]);

  const send = async () => {
    const toList = splitEmails(to);
    if (toList.length === 0) return toast.error("At least one valid To recipient is required.");
    if (!picked) return toast.error("Pick a template first.");
    setSending(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/send-email`, {
        template_key: picked,
        to: toList,
        cc: splitEmails(cc),
        bcc: splitEmails(bcc),
      });
      if (data.sent) {
        toast.success(`Email sent to ${toList.length} recipient${toList.length === 1 ? "" : "s"}`);
        onSent?.();
        onOpenChange(false);
      } else {
        toast.error(data.error || "Send failed — check email log");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const activeTpl = templates.find((t) => t.key === picked);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-600" /> Send email
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick a template, set recipients, and we'll fire it against this ticket with the customer + ticket context auto-injected.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Template <span className="text-rose-500">*</span></Label>
              <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100"
                   data-testid="send-email-template-list">
                {templates.map((t) => (
                  <button key={t.key} type="button"
                          onClick={() => setPicked(t.key)}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${picked === t.key ? "bg-indigo-50/60" : ""}`}
                          data-testid={`send-email-tpl-${t.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-800 truncate">{t.name}</span>
                      {!t.is_seed && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">Custom</Badge>}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{t.key} · {t.trigger || "manual"}</div>
                  </button>
                ))}
                {templates.length === 0 && (
                  <div className="text-xs text-slate-400 px-3 py-4 text-center">No active templates available.</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">To <span className="text-rose-500">*</span></Label>
                <Input value={to} onChange={(e) => setTo(e.target.value)}
                       placeholder="user@example.com, other@example.com"
                       data-testid="send-email-to" />
                <p className="text-[10px] text-slate-400">Comma-separated. Pre-filled from the ticket's customer email.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">CC</Label>
                  <Input value={cc} onChange={(e) => setCc(e.target.value)}
                         placeholder="(optional)"
                         data-testid="send-email-cc" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">BCC</Label>
                  <Input value={bcc} onChange={(e) => setBcc(e.target.value)}
                         placeholder="(optional)"
                         data-testid="send-email-bcc" />
                </div>
              </div>
            </div>

            {/* Preview */}
            {preview && activeTpl && (
              <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="send-email-preview">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-[11px] text-slate-600">
                  <span className="text-slate-400 mr-2">Subject:</span>
                  <span className="font-medium text-slate-800">{preview.subject}</span>
                </div>
                <iframe title="email-preview" srcDoc={preview.html}
                        className="w-full bg-white" style={{ height: 280, border: "none" }} />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending || !picked}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="send-email-fire-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sending ? "Sending…" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
